import { migrate } from './migrationRunner.ts';
import { hardenConnection, readRealmInfo, type RealmInfo } from './realmFile.ts';
import type { SqlConnection } from './sql.ts';
import { EntryEditError } from './edits.ts';

/**
 * Turns an empty database into a new realm: the full schema plus its one metadata row,
 * as the desktop's `RealmDatabase.createNew` writes it. Timestamps are ISO-8601 instants,
 * which the desktop reads with `Instant.parse`.
 */
export function initializeRealm(
  conn: SqlConnection,
  name: string,
  description: string,
  id: string = crypto.randomUUID(),
  now: string = new Date().toISOString(),
): RealmInfo {
  const trimmed = name.trim();
  if (trimmed === '') {
    throw new Error('A realm needs a name.');
  }
  hardenConnection(conn);
  migrate(conn);
  conn.run(
    'INSERT INTO realm_metadata (id, name, description, created, modified, settings) VALUES (?, ?, ?, ?, ?, ?)',
    [id, trimmed, description.trim(), now, now, ''],
  );
  return readRealmInfo(conn);
}

export const REALM_LIMITS = { name: 200, description: 5_000 } as const;

/**
 * Renames a realm and changes its description, as its overview's Edit details does. The
 * desktop reads both from `realm_metadata` when it opens the realm, so it shows them too.
 */
export function updateRealmDetails(conn: SqlConnection, name: string, description: string, now: string = new Date().toISOString()): void {
  const trimmed = name.trim();
  if (trimmed === '') throw new EntryEditError('invalid', 'A realm needs a name.');
  if (trimmed.length > REALM_LIMITS.name) throw new EntryEditError('invalid', `A realm's name can be at most ${REALM_LIMITS.name} characters.`);
  if (description.trim().length > REALM_LIMITS.description) throw new EntryEditError('invalid', `A realm's description can be at most ${REALM_LIMITS.description.toLocaleString('en')} characters.`);
  conn.run('UPDATE realm_metadata SET name = ?, description = ?, modified = ?', [trimmed, description.trim(), now]);
}

/** Gives an imported realm a new identity, for "keep both" when its id is already here. */
export function reidentifyRealm(conn: SqlConnection, newId: string, newName: string): void {
  conn.run('UPDATE realm_metadata SET id = ?, name = ?', [newId, newName]);
}
